import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { FONT_SIZE, HOLIDAY_GROUPS, INK } from "../../../theme/tokens";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import { DateInput } from "../../UIToolkit/Inputs/DateInput";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { HolidayNameField } from "./HolidayNameField";
import { HolidaySectionError } from "./HolidaySectionError";
import { HolidayShiftFields } from "./HolidayShiftFields";
import { type HolidayFirstPart, type HolidayLastPart, NEW_HOLIDAY_FIRST_PART, NEW_HOLIDAY_LAST_PART } from "./holidayApi";
import { formatHolidayDate, holidayNameLooksLikeDate, mostCommonHolidayParts } from "./holidayFormat";
import { type HolidayPreset, upcomingHolidayPresets } from "./holidayPresets";
import { UPCOMING_HOLIDAY_MONTHS, useCreateHoliday, useDepartmentToday, useHolidays } from "./holidayQueries";

const CUSTOM = "custom";
const ISO_DATE = "yyyy-MM-dd";
const presetKey = (preset: HolidayPreset) => `${preset.name}|${preset.date.toFormat(ISO_DATE)}`;

type Props = { onClose: () => void };

/** "Add holiday": a common holiday on its next date, or a custom one. The group is chosen later, in the list. */
export const HolidayAddDialog: FC<Props> = ({ onClose }) => {
	const { t, i18n } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const today = useDepartmentToday();
	const nearby = useHolidays(chosenDepartment.department_id, today.minus({ months: UPCOMING_HOLIDAY_MONTHS }), today.plus({ months: UPCOMING_HOLIDAY_MONTHS }));
	const language = i18n.language;
	// Undefined until the holidays already added have loaded — the upcoming ones decide which common holidays are left.
	const presets = useMemo(
		() =>
			nearby.data === undefined
				? undefined
				: upcomingHolidayPresets(
						today,
						nearby.data.filter((holiday) => holiday.date >= today),
						language,
					),
		[today, nearby.data, language],
	);
	const suggestedParts = useMemo(() => (nearby.data === undefined ? null : mostCommonHolidayParts(nearby.data)), [nearby.data]);
	const [pick, setPick] = useState<string | null>(null);
	// Until the scheduler picks, the first common holiday is chosen — or Custom when none is left to offer.
	const effectivePick = pick ?? (presets !== undefined && presets.length > 0 ? presetKey(presets[0]) : CUSTOM);
	const [customName, setCustomName] = useState("");
	const [customDate, setCustomDate] = useState<DateTime | undefined>(undefined);
	// Null until the scheduler picks one: until then the shifts most holidays use, or Day to Night when there are none.
	const [pickedFirstPart, setPickedFirstPart] = useState<HolidayFirstPart | null>(null);
	const [pickedLastPart, setPickedLastPart] = useState<HolidayLastPart | null>(null);
	// DEFAULT: with no holidays to match, a new holiday runs from the Day shift through the Night shift.
	const firstPart = pickedFirstPart ?? (suggestedParts === null ? NEW_HOLIDAY_FIRST_PART : suggestedParts.firstPart);
	const lastPart = pickedLastPart ?? (suggestedParts === null ? NEW_HOLIDAY_LAST_PART : suggestedParts.lastPart);
	const [missingNameOrDate, setMissingNameOrDate] = useState(false);
	const create = useCreateHoliday();

	const items = useMemo<DropdownItem<string>[]>(
		() => [
			{ value: CUSTOM, label: t("holidays.dialog.custom") },
			// DEFAULT: never read while loading — the form isn't shown until the common holidays are known.
			...(presets ?? []).map((preset) => ({
				value: presetKey(preset),
				label: t("holidays.dialog.presetOption", { name: preset.name, date: formatHolidayDate(preset.date) }),
			})),
		],
		[presets, t],
	);
	const preset = presets?.find((candidate) => presetKey(candidate) === effectivePick);
	const isCustom = preset === undefined;
	const nameLooksLikeDate = isCustom && holidayNameLooksLikeDate(customName);

	const handleConfirm = () => {
		const name = isCustom ? customName.trim() : preset.name;
		const date = isCustom ? customDate : preset.date;
		if (nameLooksLikeDate) return;
		if (name === "" || date === undefined) {
			setMissingNameOrDate(true);
			return;
		}
		create.mutate(
			{ name, date: date.toFormat(ISO_DATE), firstPart, lastPart, selectedGroupId: null },
			{
				onSuccess: (created) => {
					showSnackbar(t("holidays.dialog.added", { name: created.name }), "success");
					onClose();
				},
			},
		);
	};

	const form = (
		<>
			<SystemDropdown<DropdownItem<string>>
				items={items}
				selectedItem={effectivePick}
				onChange={(next) => {
					if (next === undefined) return;
					setPick(next);
					setMissingNameOrDate(false);
				}}
				label={t("holidays.dialog.holiday")}
				fullWidth
				menuPlacement="bottom-start"
				data-testid="holiday-dialog-preset"
			/>
			{isCustom && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
					<HolidayNameField
						value={customName}
						onChange={(name) => {
							setCustomName(name);
							setMissingNameOrDate(false);
						}}
						placeholder={t("holidays.dialog.namePlaceholder")}
						autoFocus
						dataTestId="holiday-dialog-custom-name"
					/>
					<DateInput
						label={t("date")}
						value={customDate}
						onChange={(date) => {
							setCustomDate(date.startOf("day"));
							setMissingNameOrDate(false);
						}}
						minDate={today}
						formControlSx={{ width: "100%" }}
						dataTestId="holiday-dialog-custom-date"
					/>
				</Box>
			)}
			{missingNameOrDate && (
				<InlineHint tone="warning" dataTestId="holiday-dialog-missing">
					{t("holidays.dialog.missingNameDate")}
				</InlineHint>
			)}
			<HolidayShiftFields
				firstPart={firstPart}
				lastPart={lastPart}
				// Picking one shift keeps the other as shown, so a reload of the holidays can't change it.
				onFirstPartChange={(part) => {
					setPickedFirstPart(part);
					setPickedLastPart(lastPart);
				}}
				onLastPartChange={(part) => {
					setPickedFirstPart(firstPart);
					setPickedLastPart(part);
				}}
				// Said only while both shifts are still the ones matched to the other holidays.
				note={suggestedParts !== null && pickedFirstPart === null && pickedLastPart === null ? t("holidays.dialog.partsMatched") : undefined}
			/>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("holidays.dialog.groupLater")}</Typography>
		</>
	);

	// The form waits for the holidays, so neither the pick nor the shifts jump mid-typing.
	const renderBody = () => {
		if (nearby.isError) return <HolidaySectionError onRetry={() => nearby.refetch()} dataTestId="holiday-add-error" />;
		if (presets === undefined) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }} data-testid="holiday-add-loading">
					<JumpingDots size={6} />
				</Box>
			);
		}
		return form;
	};

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1, width: { sm: HOLIDAY_GROUPS.FORM_DIALOG_BODY_WIDTH_PX } }}>{renderBody()}</Box>
	);

	return (
		<SystemDialog
			open
			onClose={onClose}
			title={t("holidays.dialog.addTitle")}
			body={body}
			cancel={t("cancel")}
			confirm={t("holidays.add")}
			confirmButtonVariant="primary"
			onConfirm={handleConfirm}
			confirmDisabled={presets === undefined || nameLooksLikeDate}
			confirmLoading={create.isPending}
			dataTestId="holiday-add-dialog"
			confirmDataTestId="holiday-add-confirm"
			cancelDataTestId="holiday-add-cancel"
		/>
	);
};
